import React, { useState, useEffect } from 'react';
import { Map, TileLayer, Circle } from 'react-leaflet';
import DetailSheet from './components/DetailSheet';
import { getCases } from './api';
import colors from './theme';
import './App.css';

const calcRegionStats = region => {
  return {
    confirmed: region.confirmed,
    recovered: region.recovered,
    dead: region.dead,
    active: region.confirmed - region.recovered - region.dead,
    lastUpdate: region.last_update,
  };
};

const calcGlobalStats = cases => {
  let initialStats = {
    confirmed: 0,
    dead: 0,
    recovered: 0,
    lastUpdate: -1,
  };
  // TODO: remove when API returns lastUpdate
  return cases.regions.reduce((acc, r) => {
    acc.confirmed += r.confirmed;
    acc.dead += r.dead;
    acc.recovered += r.recovered;
    acc.active = acc.confirmed - acc.dead - acc.recovered;
    if (r.last_update > acc.lastUpdate) {
      acc.lastUpdate = r.last_update;
    }
    return acc;
  }, initialStats);
};

const calcRadius = (n, zoom) => (50000 + 0.25 * n) / Math.pow(1.875, zoom - 3);

function App() {
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState({
    loading: true,
    error: null,
    cases: null,
    zoom: 2,
  });

  useEffect(() => {
    async function refresh() {
      setState(state => ({
        ...state,
        loading: true,
        error: null,
      }));
      try {
        const cases = await getCases();
        setState(state => ({
          ...state,
          loading: false,
          error: null,
          cases,
        }));
      } catch (e) {
        console.error(e);
        setState(state => ({
          ...state,
          loading: false,
          error: new Error('Failed to fetch data'),
        }));
      }
    }

    refresh();
  }, []);

  const { cases, loading, error, zoom } = state;
  let overlays, stats;
  if (cases) {
    overlays = cases.regions.map((r, i) => (
      <Circle
        center={{ lat: r.latitude, lng: r.longitude }}
        radius={calcRadius(r.confirmed, zoom)}
        color={colors.circleOverlayStroke}
        weight={1}
        fillColor={
          r === selected
            ? colors.circleOverlayFillSelected
            : colors.circleOverlayFill
        }
        fillOpacity={1}
        // onMouseOver={(_) => setSelected(r)}
        onClick={e => {
          setSelected(r);
          window.gtag('event', 'select_content', {
            content_id: r.province || r.country,
          });
        }}
        key={i}
      />
    ));

    stats = selected ? calcRegionStats(selected) : calcGlobalStats(cases);
  }

  return (
    <>
      {error && (
        <div className="error-box" style={styles.error}>
          <h1 style={{ fontSize: 12, paddingLeft: 8, paddingRight: 8 }}>
            {error.toString()}
          </h1>
        </div>
      )}
      <DetailSheet
        region={
          (selected && (selected.province || selected.country)) || 'Global'
        }
        {...stats}
        loading={loading}
        error={error}
      />
      <Map
        center={[0, -35]}
        maxBounds={[
          [-90, -360],
          [90, 360],
        ]}
        zoom={zoom}
        minZoom={2}
        zoomControl={false}
        style={styles.map}
        onClick={e => {
          // If the user clicks / taps on the map itself, not one of our
          // feature overlays, then unselect any currently selected overlay.
          if (e.originalEvent.target.tagName !== 'path') {
            setSelected(null);
          }
        }}
        onZoom={e => setState({ ...state, zoom: e.target._zoom })}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {overlays}
      </Map>
    </>
  );
}

const styles = {
  map: {
    zIndex: 0,
  },
  error: {
    zIndex: 1,
    position: 'absolute',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: colors.red,
    color: colors.textHigh,
    borderRadius: 4,
  },
};

export default App;
